/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { FunctionComponent } from "react";

import { StrapiPricingPage } from "@/types/strapi";

import { ColumnGaps } from "@/tokens/spacing";

import { GridColumn } from "@/ui/atoms/grid_column";
import { GridContainer } from "@/ui/atoms/grid_container";
import { PricingPlanCard } from "@/ui/atoms/pricing_plan_card";
import { Text } from "@/ui/atoms/text";
import { RichText } from "@/ui/molecules/rich_text";

import { buildStylesByBreakpoint } from "@/util/style_util";

export const PricingHero: FunctionComponent<StrapiPricingPage> = (props) => {
    /**
     * Styles
     */

    const planCardsContainerStyles = css(
        { display: "flex", justifyContent: "space-between" },
        buildStylesByBreakpoint("gap", ColumnGaps),
        buildStylesByBreakpoint("flexDirection", {
            extraSmall: "column",
            medium: "row",
        }),
        buildStylesByBreakpoint("flexWrap", {
            extraSmall: "nowrap",
            medium: "wrap",
            large: "nowrap",
        }),
    );

    const planCardStyles = css(
        {
            flexGrow: 1,
        },
        buildStylesByBreakpoint("flexBasis", {
            extraSmall: 1,
            medium: "35%", // Forces two-per-line wrap
            large: 1,
        }),
    );

    /**
     * Rendering
     */
    const renderPlanCards = () => {
        const { Plans } = props;

        if (!Plans) {
            return;
        }

        return (
            <GridColumn
                className={planCardsContainerStyles}
                columnSpan={{
                    extraSmall: "full",
                    medium: 8,
                    large: "full",
                    extraLarge: 10,
                }}
                columnStart={{
                    extraSmall: 1,
                    medium: 3,
                    large: 1,
                    extraLarge: 2,
                }}
            >
                {Plans.map((plan, index) => (
                    <PricingPlanCard
                        {...plan}
                        className={planCardStyles}
                        index={index}
                        isEmphasized={
                            props.Emphasized_Plan &&
                            plan.id === props.Emphasized_Plan.id
                        }
                        key={`pricing-hero::plan-card::${plan.id}`}
                    />
                ))}
            </GridColumn>
        );
    };

    return (
        <GridContainer>
            <GridColumn
                className={css({
                    alignItems: "center",
                    display: "flex",
                    flexDirection: "column",
                })}
                columnSpan={{
                    extraSmall: 6,
                    medium: 6,
                    large: 8,
                }}
                columnStart={{
                    extraSmall: 1,
                    medium: 4,
                    large: 3,
                }}
                marginTop="Default"
            >
                <Text
                    fontSize="HeadlineDefault"
                    marginBottom="LongformHeadlineLarge"
                    marginTop="Default"
                    tag="h1"
                    textAlign="center"
                >
                    {props.Headline}
                </Text>
            </GridColumn>

            {renderPlanCards()}

            <GridColumn
                className={css({
                    textAlign: "center",
                })}
                columnSpan={6}
                columnStart={{
                    extraSmall: 1,
                    medium: 4,
                }}
                marginTop="PricingParagraphMargin"
            >
                {props.Plan_Cards_Footer && (
                    <RichText
                        className={css({ "*>a": { display: "inline-block" } })}
                        config="Longform"
                    >
                        {props.Plan_Cards_Footer}
                    </RichText>
                )}
            </GridColumn>
        </GridContainer>
    );
};
